import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import intl from 'react-intl-universal';
import {
  Modal,
  Dropdown,
  Typography,
  FormItem,
  tailwindOverride,
} from '@getsynapse/design-system';
import { useAppThunkDispatch } from 'state/store';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  fetchProjectTemplatesForSelection,
  selectAllProjectTemplates,
  selectProjectTemplatesStatus,
} from 'state/ProjectTemplates/projectTemplatesSlice';
import { PATHS, SLICE_STATUS } from 'utils/constants';
import { ProjectTemplate } from 'types/store/normalized';
import AddProjectIcon from 'assets/icons/add-project-icon.svg';

const CreateProjectModal: React.FC<{
  showModal: boolean;
  onCloseModal: () => void;
}> = ({ showModal = false, onCloseModal }) => {
  const dispatch = useAppThunkDispatch();
  const history = useHistory();
  const { teamScopingProjectTemplates = false } = useFlags();
  const [shouldDisplayModal, setShouldDisplayModal] = useState<boolean>(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const projectTemplates = useSelector(selectAllProjectTemplates);
  const templatesStatus = useSelector(selectProjectTemplatesStatus);
  const isLoadingTemplates = templatesStatus === SLICE_STATUS.LOADING;

  useEffect(() => {
    if (showModal && !shouldDisplayModal) {
      dispatch(
        fetchProjectTemplatesForSelection({
          useNewApi: teamScopingProjectTemplates,
        })
      );
      setShouldDisplayModal(showModal);
    }
  }, [showModal, shouldDisplayModal, dispatch, teamScopingProjectTemplates]);

  const handleCloseModal = () => {
    onCloseModal();
    setShouldDisplayModal(false);
  };

  const handleCreateProject = () => {
    history.push(`${PATHS.NEW_PROJECT_PAGE}/${selectedTemplate}`);
  };

  return (
    <Modal
      title={intl.get('PROJECTS_LIST_PAGE.CREATE_PROJECT_MODAL.TITLE')}
      titleIcon={{ src: AddProjectIcon }}
      isOpen={shouldDisplayModal}
      size='large'
      closeModal={handleCloseModal}
      aria-label={intl.get('PROJECTS_LIST_PAGE.CREATE_PROJECT_MODAL.TITLE')}
      actionButtons={[
        {
          children: intl.get('CREATE'),
          variant: 'primary',
          disabled: !selectedTemplate,
          'data-testid': 'create-project-button',
          onClick: handleCreateProject,
          className: tailwindOverride({
            'cursor-not-allowed': !selectedTemplate,
          }),
        },
      ]}
      data-testid='create-project-modal'
    >
      <Typography variant='body' className='mb-4 text-primary'>
        {intl.get('PROJECTS_LIST_PAGE.CREATE_PROJECT_MODAL.BODY')}
      </Typography>
      <FormItem
        label={intl.get(
          'PROJECTS_LIST_PAGE.CREATE_PROJECT_MODAL.TEMPLATE_FIELD_LABEL'
        )}
        labelProps={{ required: true }}
        className='w-88'
      >
        <Dropdown
          disabled={isLoadingTemplates}
          options={projectTemplates}
          getOptionLabel={(option: ProjectTemplate) => option.name}
          placeholder={intl.get('SELECT')}
          onChange={(option: ProjectTemplate) =>
            setSelectedTemplate(option.id!)
          }
          listProps={{ 'data-testid': 'templates-list' }}
          triggerProps={{ 'data-testid': 'select-template' }}
          renderOption={(
            option: ProjectTemplate,
            isSelected: boolean,
            selectOption,
            { className, ...otherProps }
          ) => (
            <li
              {...otherProps}
              data-value={option.id}
              data-testid={option.id}
              key={option.id}
              className={tailwindOverride('group', className, {
                'hover:bg-primary focus-visible:bg-primary': isSelected,
              })}
              onClick={selectOption}
            >
              <div className='flex w-100 flex-col space-y-1 '>
                <Typography
                  variant='label'
                  weight='medium'
                  className='text-primary'
                >
                  {option.name}
                </Typography>
                {option.description && (
                  <Typography variant='caption' className='text-neutral'>
                    {option.description}
                  </Typography>
                )}
              </div>
            </li>
          )}
        />
      </FormItem>
    </Modal>
  );
};

export default CreateProjectModal;
